import { Button, Card, Col, Row, Skeleton, Space, Typography } from "antd";
import { App, Component } from "../../types";
import GitCommitLink from "../../components/GitCommitLink";
import MessageCard from "../../components/cards/MessageCard";
import { Crown, Globe, Server, GitBranch, Copy, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { trackAssignDomainInit } from "../../backend/tracking/services";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { useNavigate, useParams } from "react-router";
import FargateLogo from "../../assets/fargate.png";
import { getEnvironmentService } from "../../backend";

const { Text, Paragraph } = Typography;

interface AppOverviewProps {
    component: Component;
    app: App;
}

export default function AppOverview({
    component,
    app
}: AppOverviewProps) {
    const navigate = useNavigate();
    const { projectId, envId } = useParams() as { projectId: string; envId: string; }
    const currentProject = useRecoilValue(currentProjectState);
    const [domainDisplaySettings, setDomainDisplaySettings] = useState<{
        ctaCard: boolean;
        assignCustomDomainBtn: boolean;
        displayDomain: boolean;
    }>();
    const [domain, setDomain] = useState<{ name: string; active: boolean; }>();

    const checkIfDomainIsWorking = (kubeConfig: any[]) => {
        // ingress-tls is used as a default secretName for shared clusters; 
        // ingress-tls is part of the certificate resource in a personal cluster => domain is not yet configured
        const hasCertificate = !!kubeConfig.find(r => r.kind === "Certificate" && r.apiVersion === "cert-manager.io/v1" && r.spec?.issuerRef?.kind === "ClusterIssuer" && r.spec?.secretName !== "ingress-tls");
        return hasCertificate;
    };

    useEffect(() => {
        /**
            * free & no domain                     - MessageCard
            * free & domain (microtica.rocks)      - MessageCard - display domain
            * free & domain (custom)               - NOT POSSIBLE
            * starter & no domain                  - Button
            * starter & domain (microtica.rocks)   - display domain
            * starter & domain (custom)            - display domain
        */

        const load = async () => {
            if (component.type === "kubernetes") {
                const domainName = app.deployment?.configurations.find(c => c.key === "MIC_DOMAIN_NAME")?.value || "n/a";
                const isDomainActive = checkIfDomainIsWorking(JSON.parse(app.deployment?.kubeConfig || "[{}]"));

                if (currentProject.paymentPlan?.id === "FREE") {
                    // Free plan project
                    if (isDomainActive) {
                        // Active domain
                        if (domainName === `${app.name}-${app.namespace}.microtica.rocks`) {
                            // Free plan & Active Microtica domain
                            setDomainDisplaySettings({
                                ctaCard: true,
                                assignCustomDomainBtn: false,
                                displayDomain: true
                            });
                        }
                    } else {
                        // Free plan & No domain
                        setDomainDisplaySettings({
                            ctaCard: true,
                            assignCustomDomainBtn: false,
                            displayDomain: false
                        });
                    }
                } else {
                    // Paid plan project
                    if (isDomainActive) {
                        // Paid plan & any domain
                        setDomainDisplaySettings({
                            ctaCard: false,
                            assignCustomDomainBtn: false,
                            displayDomain: true
                        });
                    } else {
                        // Paid plan & No domain
                        setDomainDisplaySettings({
                            ctaCard: false,
                            assignCustomDomainBtn: true,
                            displayDomain: false
                        });
                    }
                }
                setDomain({
                    name: `https://${domainName}`,
                    active: isDomainActive
                });
            } else {
                const { data: { outputs } } = await getEnvironmentService().getOutputsForResource(envId, component.name, projectId);
                const accessUrl = outputs?.find(o => o.key === "AccessUrl")?.value || "";
                const CNAME = outputs?.find(o => o.key === "CNAME")?.value;
                const displayAssignDomainBtn = !!accessUrl && !!CNAME && new URL(accessUrl).hostname === CNAME.toLowerCase();

                if (currentProject.paymentPlan?.id === "FREE" && displayAssignDomainBtn) {
                    setDomainDisplaySettings({
                        ctaCard: true,
                        assignCustomDomainBtn: true,
                        displayDomain: !!accessUrl
                    });
                } else if (currentProject.paymentPlan?.id !== "FREE" && displayAssignDomainBtn) {
                    setDomainDisplaySettings({
                        ctaCard: false,
                        assignCustomDomainBtn: true,
                        displayDomain: !!accessUrl
                    });
                } else {
                    setDomainDisplaySettings({
                        ctaCard: false,
                        assignCustomDomainBtn: false,
                        displayDomain: !!accessUrl
                    });
                }
                setDomain({
                    name: accessUrl,
                    active: true
                });
            }
        }

        if (app) {
            load();
        }
    }, [app]);


    const CTA = () => (
        <MessageCard
            type="cta"
            icon={
                <Globe size={26} style={{ color: "#3399ff" }} />
            }
            text={
                <div>
                    <div style={{ fontWeight: 600 }}>Custom domain</div>
                    <div>
                        Set a custom domain to enhance your app's appeal and improve your customers' experience.
                    </div>
                </div>
            }
            extra={
                <Button
                    type="primary"
                    onClick={() => {
                        navigate(`/projects/${projectId}/environments/${envId}/components/${component.id}/${app.clusterId}/apps/${app.namespace}/${app.name}/settings/domain`);
                        trackAssignDomainInit({
                            serviceName: app.name,
                            clusterName: app.clusterId
                        });
                    }}
                >
                    <Crown size={16} /> Assign domain
                </Button>
            }
        />
    )

    const ClusterInfo = () => (
        <Card size="small" bodyStyle={{ padding: '16px' }}>
            <Space size="middle" style={{ width: '100%' }}>
                <div 
                    className="flex-align-center"
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        background: component.type === "kubernetes" 
                            ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' 
                            : '#fff1f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: component.type === "kubernetes" 
                            ? '0 2px 8px rgba(24, 144, 255, 0.2)' 
                            : 'none'
                    }}
                >
                    {
                        component.type === "kubernetes" ?
                            <Server size={20} style={{ color: "#ffffff" }} /> :
                            <img src={FargateLogo} style={{ width: "20px", height: "20px" }} alt="fargate" />
                    }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Cluster ID
                            </Text>
                            <div style={{ marginTop: '2px' }}>
                                <Paragraph 
                                    copyable={{ 
                                        text: app.clusterId,
                                        tooltips: ['Copy', 'Copied'],
                                        icon: [<Copy size={14} key="copy" />, <Check size={14} key="copied" />]
                                    }}
                                    style={{ 
                                        margin: 0,
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {app.clusterId}
                                </Paragraph>
                            </div>
                        </div>
                        {
                            app.namespace &&
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Namespace
                                </Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Paragraph 
                                        copyable={{ 
                                            text: app.namespace,
                                            icon: [<Copy size={14} key="copy" />, <Check size={14} key="copied" />]
                                        }}
                                        style={{ 
                                            margin: 0,
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {app.namespace}
                                    </Paragraph>
                                </div>
                            </div>
                        }
                    </Space>
                </div>
            </Space>
        </Card>
    )

    const GitInfo = () => (
        <Card size="small" bodyStyle={{ padding: '16px' }}>
            <Space size="middle" style={{ width: '100%' }}>
                <div 
                    className="flex-align-center"
                    style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #ff7875 0%, #ff4d4f 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: '0 2px 8px rgba(255, 77, 79, 0.2)'
                    }}
                >
                    <GitBranch size={20} style={{ color: "#ffffff" }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        {app.deployment?.message && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Commit Message
                                </Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text 
                                        ellipsis={{ tooltip: app.deployment?.message }} 
                                        style={{ 
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            display: 'block'
                                        }}
                                    >
                                        {app.deployment?.message}
                                    </Text>
                                </div>
                            </div>
                        )}
                        {app.deployment?.repository && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Repository
                                </Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text 
                                        style={{ 
                                            fontSize: '13px',
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        <a 
                                            href={app.deployment?.repository} 
                                            target="_blank" 
                                            rel="noreferrer"
                                        >
                                            {app.deployment?.repository}
                                        </a>
                                    </Text>
                                </div>
                            </div>
                        )}
                        {(app.deployment?.sha || app.deployment?.version) && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Commit
                                </Text>
                                <div style={{ marginTop: '2px' }}>
                                    <GitCommitLink
                                        version={app.deployment?.sha || app.deployment?.version}
                                        repoUrl={app.deployment?.repository}
                                        branch={app.deployment?.commitName}
                                    />
                                </div>
                            </div>
                        )}
                    </Space>
                </div>
            </Space>
        </Card>
    )

    const DomainInfo = () => {
        return !domainDisplaySettings ? <Skeleton.Input /> :
            <Card size="small" bodyStyle={{ padding: '16px' }}>
                <Space size="middle" style={{ width: '100%' }} direction="vertical">
                    <Space size="middle" style={{ width: '100%' }}>
                        <div 
                            className="flex-align-center"
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '6px',
                                background: domainDisplaySettings?.displayDomain 
                                    ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' 
                                    : 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: domainDisplaySettings?.displayDomain 
                                    ? '0 2px 8px rgba(24, 144, 255, 0.2)' 
                                    : '0 2px 8px rgba(250, 173, 20, 0.2)'
                            }}
                        >
                            <Globe size={20} style={{ 
                                color: "#ffffff"
                            }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {
                                domainDisplaySettings?.displayDomain ? (
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Domain URL
                                            </Text>
                                            <div style={{ marginTop: '2px' }}>
                                                <Paragraph 
                                                    copyable={{ 
                                                        text: domain?.name || '',
                                                        icon: [<Copy size={14} key="copy" />, <Check size={14} key="copied" />]
                                                    }}
                                                    style={{ 
                                                        margin: 0,
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    <a 
                                                        href={domain?.name} 
                                                        target="_blank" 
                                                        rel="noreferrer"
                                                    >
                                                        {domain?.name}
                                                    </a>
                                                </Paragraph>
                                            </div>
                                        </div>
                                    </Space>
                                ) : (
                                    <Text type="secondary" style={{ fontSize: '13px' }}>
                                        Assign a custom domain to make your application accessible on the internet with a professional URL.
                                    </Text>
                                )
                            }
                        </div>
                    </Space>
                    {
                        domainDisplaySettings?.assignCustomDomainBtn &&
                        <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                                type="primary"
                                onClick={() => {
                                    if (component.type === "kubernetes") {
                                        navigate(`/projects/${projectId}/environments/${envId}/components/${component.id}/${app.clusterId}/apps/${app.namespace}/${app.name}/settings/domain`);
                                        trackAssignDomainInit({
                                            serviceName: app.name,
                                            clusterName: app.clusterId
                                        });
                                    } else {
                                        navigate(`/projects/${projectId}/environments/${envId}/components/${component.id}/settings/domain`);
                                    }
                                }}
                            >
                                <Crown size={16} /> {component.type === "kubernetes" ? "Assign Domain" : "Assign Custom Domain"}
                            </Button>
                        </div>
                    }
                </Space>
            </Card>
    }


    return (
        <Space direction="vertical" size="large" className="full-width">
            <div className="flex-justify-space-between">
                <div style={{ fontWeight: 600, fontSize: 16 }}>
                    Application Overview
                    <div className="gray-text" style={{ fontWeight: 400, fontSize: 14 }}>
                        Cluster configuration, deployment details, and domain settings for this application.
                    </div>
                </div>
            </div>
            {
                domainDisplaySettings?.ctaCard &&
                <Row gutter={24} style={{ marginBottom: 20 }}>
                    <Col xs={24}>
                        <CTA />
                    </Col>
                </Row>
            }
            <Space direction="vertical" size={12} className="full-width">
                {/* Cluster details */}
                <ClusterInfo />

                {/* Git commit info */}
                <GitInfo />

                {/* Domain info */}
                <DomainInfo />
            </Space>
        </Space>
    )
}